import { DragEvent, ReactNode, RefObject } from 'react';

import { BackrsChip, ContentNugget, NUGGET_THEMES } from '@backrs_ui/common-ui';

import { startNuggetDrag } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  getDraggableNuggetId,
  VARIANT_TO_RESOURCE_KEY,
} from '@backrs_ui/grow-ui/src/models/models';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, IconButton, SxProps, Theme } from '@mui/material';

import { NuggetCardItem } from './NuggetCardItem';

type DirectionType = 'row' | 'column' | 'scrollingRow';

const handleDragStart = (event: DragEvent<HTMLDivElement>, nugget: ContentNugget) => {
  const resourceKey = VARIANT_TO_RESOURCE_KEY[nugget.variant];

  if (!resourceKey) return;

  startNuggetDrag(event, {
    id: getDraggableNuggetId(nugget, resourceKey),
    type: resourceKey,
    title: nugget.title,
  });
};

type NuggetCardListProps = Readonly<{
  columnHeader?: string;
  nuggets: ContentNugget[];
  onClick: (nugget: ContentNugget) => void;
  direction?: DirectionType;
  columnRef?: RefObject<HTMLDivElement>;
  handleScrollToNextColumn?: () => void;
  additionalNuggetComponents?: ReactNode[];
  hideActions?: boolean;
}>;

export const NuggetCardList = ({
  columnHeader,
  nuggets,
  onClick,
  direction = 'column',
  columnRef,
  handleScrollToNextColumn,
  additionalNuggetComponents = [],
  hideActions,
}: NuggetCardListProps) => {
  const isActivity = columnHeader === NUGGET_THEMES.GROW_BY_DOING;

  const renderedNuggets = nuggets.map((nugget: ContentNugget) => {
    const isDraggable = !!VARIANT_TO_RESOURCE_KEY[nugget.variant];

    return (
      <Box key={`${nugget.type}-${nugget.variant}-${nugget.id}`} sx={getNuggetStyles(direction)}>
        <div
          id={`nugget-list-card-${nugget.variant}-${nugget.id}`}
          draggable={isDraggable}
          onDragStart={isDraggable ? (event) => handleDragStart(event, nugget) : undefined}
          style={isDraggable ? { cursor: 'grab' } : undefined}
        >
          <NuggetCardItem nugget={nugget} onClick={onClick} hideActions={hideActions} />
        </div>
      </Box>
    );
  });

  const renderedAdditionalComponents = additionalNuggetComponents.map((component, index) => (
    <Box key={`additional-component-${index}`} sx={getNuggetStyles(direction)}>
      {component}
    </Box>
  ));

  const allRenderedItems = [...renderedAdditionalComponents, ...renderedNuggets];

  if (direction === 'column') {
    return (
      <Box sx={columnContentContainerStyle}>
        {columnHeader && (
          <Box ref={columnRef} sx={stickyHeaderStyle(isActivity)}>
            <BackrsChip
              label={columnHeader}
              variant={{
                type: 'LARGE_TEXT',
                backgroundColor: 'BLUE',
              }}
            />
            {handleScrollToNextColumn && (
              <IconButton
                id="nugget-card-list-scroll-next-button"
                onClick={handleScrollToNextColumn}
                sx={stickyArrowButtonStyle}
              >
                <KeyboardArrowDown sx={stickyArrowStyle} />
              </IconButton>
            )}
          </Box>
        )}
        <Box sx={nuggetColumnContainerStyle}>{allRenderedItems}</Box>
      </Box>
    );
  }

  if (direction === 'scrollingRow') {
    return <Box sx={horizontalScrollContainerStyle}>{allRenderedItems}</Box>;
  }

  return (
    <Box>
      {columnHeader && (
        <BackrsChip
          label={columnHeader}
          variant={{
            type: 'LARGE_TEXT',
            backgroundColor: 'BLUE',
          }}
        />
      )}
      <Box sx={rowColumnContainerStyle}>
        <Box sx={nuggetRowContainerStyle}>{allRenderedItems}</Box>
      </Box>
    </Box>
  );
};

const columnContentContainerStyle: SxProps<Theme> = {
  maxWidth: '70vw',
  minWidth: '300px',
  '@media (max-width: 1200px)': {
    minWidth: 'unset',
    maxWidth: 'unset',
  },
};

const rowColumnContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  boxSizing: 'border-box',
});

const nuggetRowContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gap: backrs.spacing['spacing-2xl'],
  height: '100%',
  gridTemplateColumns: `repeat(auto-fill, minmax(min(250px, 100%), 282px))`,
});

const nuggetColumnContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gap: backrs.spacing['spacing-3xl'],
  height: '100%',
});

const stickyHeaderStyle =
  (isActivity: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    '@media (max-width: 837px)': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      backgroundColor: isActivity ? backrs.colors['BlueLight/100'] : backrs.colors['Beige/50'],
      py: backrs.spacing['spacing-xl'],
      justifyContent: 'space-between',
      display: 'flex',
      alignItems: 'center',
    },
  });

const stickyArrowButtonStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'none',
  '@media (max-width: 837px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: backrs.colors['Colors/Base/White'],
    borderRadius: '50%',
    border: `1px solid ${backrs.colors['Brand/200']}`,
    height: backrs.spacing['spacing-4xl'],
    width: backrs.spacing['spacing-4xl'],
  },
});

const stickyArrowStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
  cursor: 'pointer',
});

const horizontalScrollContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: backrs.spacing['spacing-3xl'],
  containerType: 'inline-size',
  padding: backrs.spacing['spacing-md'],
  margin: `calc(-1 * ${backrs.spacing['spacing-md']})`,
  '@container (max-width: 600px)': {
    scrollSnapType: 'x mandatory',
  },
});

const horizontalScrollNuggetStyle: SxProps<Theme> = {
  minWidth: '274px',
  maxWidth: '274px',
  flexShrink: 0,
  '@container (max-width: 600px)': {
    scrollSnapAlign: 'center',
  },
};

const renderedRowNuggetStyle: SxProps<Theme> = {
  width: {
    xs: '100%',
    sm: 'unset',
  },
};

const getNuggetStyles = (direction: DirectionType): SxProps<Theme> => {
  const styleMap: Record<DirectionType, SxProps<Theme>> = {
    row: renderedRowNuggetStyle,
    scrollingRow: horizontalScrollNuggetStyle,
    column: {},
  };

  return styleMap[direction];
};
